import {
  Component,
  ChangeDetectionStrategy,
  Input,
  OnChanges,
  SimpleChanges,
  SecurityContext,
  OnDestroy,
  NgZone,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { AkitaFeaturedQuery } from '@app/akita/api/featured/state/featured.query';
import {
  colorNameToTranslationLabel,
  Product,
} from '@app/akita/api/products/models/product.model';
import { AkitaProductsQuery } from '@app/akita/api/products/state/products.query';
import { AkitaProductsService } from '@app/akita/api/products/state/products.service';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { TranslocoService } from '@ngneat/transloco';
import { addMonths } from 'date-fns';
import { BehaviorSubject, Observable, Subject, Subscription, takeUntil } from 'rxjs';
import {
  AggregateRating,
  Offer,
  Product as ProductSchema,
  Review,
  WithContext,
} from 'schema-dts';
import { NgxJsonLdComponent } from '../../../ngx-json-ld/ngx-json-ld.component';
import { AsyncPipe } from '@angular/common';
import { getCanonicalProductUrl } from '@app/shared/utils/url.utils';

const EXPIRING_MONTHS = 12;
const OLD_GTIN_12_LENGTH = 12;

@Component({
  selector: 'app-product-json-ld',
  templateUrl: './product-json-ld.template.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgxJsonLdComponent, AsyncPipe],
})
export class ProductJsonLdComponent implements OnDestroy, OnChanges {
  @Input() public lang?: string | null;
  @Input() public country?: string | null;
  @Input() public productId?: string | null;
  @Input() public product?: Product | null;

  private readonly jsonLdSchemaSubject: BehaviorSubject<WithContext<ProductSchema>>;
  public jsonLdSchema$: Observable<WithContext<ProductSchema>>;

  private readonly productSubject: BehaviorSubject<Product | null>;

  private readonly subscriptions: Subscription;
  private readonly destroySubject: Subject<void>;

  constructor(
    private readonly zone: NgZone,
    private readonly domSanitizer: DomSanitizer,
    private readonly translateService: TranslocoService,
    private readonly akitaRouterQuery: AkitaRouterQuery,
    private readonly akitaFeaturedQuery: AkitaFeaturedQuery,
    private readonly akitaProductsQuery: AkitaProductsQuery,
    private readonly akitaProductsService: AkitaProductsService
  ) {
    this.destroySubject = new Subject();
    this.subscriptions = new Subscription();

    this.productSubject = new BehaviorSubject(this.product || null);

    this.jsonLdSchemaSubject = new BehaviorSubject(this.updateJSONLd(this.product));
    this.jsonLdSchema$ = this.jsonLdSchemaSubject.asObservable();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.product) {
      this.refreshProduct(this.product || null);
    } else if (changes.productId) {
      const product = this.akitaProductsQuery.getProduct(
        this.productId,
        this.lang,
        this.country
      );
      if (product || !this.productId) {
        this.refreshProduct(this.product || null);
      } else {
        this.fetchProduct(this.productId);
      }
    }
  }

  private fetchProduct(productId: string): void {
    if (this.akitaRouterQuery.isBrowser) {
      this.zone.runOutsideAngular(() => {
        this.subscriptions.add(
          this.akitaProductsService
            .getProduct(productId)
            .pipe(takeUntil(this.destroySubject))
            .subscribe({
              next: this.refreshProduct.bind(this),
              error: () => {},
            })
        );
      });
    } else {
      this.subscriptions.add(
        this.akitaProductsService
          .getProduct(productId)
          .pipe(takeUntil(this.destroySubject))
          .subscribe({
            next: this.refreshProduct.bind(this),
            error: () => {},
          })
      );
    }
  }

  private refreshProduct(product: Product | null): void {
    this.productSubject.next(product);
    this.monitorReviews(product);
    this.jsonLdSchemaSubject.next(this.updateJSONLd(product));
  }

  private monitorReviews(product?: Product | null): void {
    if (product) {
      const phoneLanguage = `${
        product?.language || this.translateService.getActiveLang() || 'en'
      }`;
      const phoneModel = `${product?.model || ''}`;

      this.subscriptions.add(
        this.akitaFeaturedQuery
          .selectFeaturedReviews(phoneLanguage, phoneModel)
          .pipe(takeUntil(this.destroySubject))
          .subscribe({
            next: () => {
              this.jsonLdSchemaSubject.next(this.updateJSONLd(this.product));
            },
          })
      );
    }
  }

  private updateJSONLd(product?: Product | null): WithContext<ProductSchema> {
    const jsonLdSchema: WithContext<ProductSchema> = {
      '@context': 'https://schema.org',
      '@type': 'Product',
    };

    if (product?.id) {
      jsonLdSchema.sku = product?.id || '';
      jsonLdSchema.name = product?.title || '';
      jsonLdSchema.description =
        product?.description || this.getPlaceholderDescription(product);
      jsonLdSchema.url = `${this.domSanitizer.sanitize(SecurityContext.URL, getCanonicalProductUrl(product) || '')}`;

      if (product?.mpn) {
        jsonLdSchema.mpn = product.mpn;
      }

      if (product?.upc) {
        jsonLdSchema.gtin13 =
          product.upc.length === OLD_GTIN_12_LENGTH ? `0${product.upc}` : product.upc;
      }

      if (product?.model) {
        jsonLdSchema.model = `${product.model}`;
      }

      if (product?.color) {
        jsonLdSchema.color = this.translateService.translate(
          colorNameToTranslationLabel(`${product.color}`)
        );
      }

      if (product?.brand) {
        jsonLdSchema.brand = {
          '@type': 'Brand',
          name: `${product.brand}`,
        };
      } else {
        const model = `${product?.model || ''}`.toUpperCase();
        if (model.includes('IPHONE')) {
          jsonLdSchema.brand = {
            '@type': 'Brand',
            name: `Apple`,
          };
        }
      }

      jsonLdSchema.aggregateRating = this.generateAggregateRating(product) || undefined;
      jsonLdSchema.image = this.generatePictures(product);
      jsonLdSchema.offers = this.generateOffers(product);
      jsonLdSchema.review = this.generateReviews(product);
    }

    return jsonLdSchema;
  }

  private generatePictures(product?: Product | null): Array<string> {
    const pictures = new Array(0);
    if (product) {
      for (const image of product.images) {
        if (image) {
          pictures.push(image);
        }
      }
    }
    return pictures;
  }

  private generateOffers(product?: Product | null): Array<Offer> {
    const offers = new Array(0);

    if (product) {
      const offer: Offer = {
        '@type': 'Offer',
        itemCondition: 'RefurbishedCondition',
        availability: product.inStock ? 'InStock' : 'OutOfStock',
        url: `${this.domSanitizer.sanitize(SecurityContext.URL, getCanonicalProductUrl(product) || '')}`,
        price: `${product?.price?.amount || '0.1'}`,
        priceCurrency: product?.price?.currency || 'USD',
        priceValidUntil: this.priceValidUntil,
        offeredBy: 'Popsy LLC',

        shippingDetails: {
          '@type': 'OfferShippingDetails',
          shippingRate: {
            '@type': 'MonetaryAmount',
            value: '0.00',
            currency: product?.price?.currency || 'USD',
          },
          shippingDestination: {
            '@type': 'DefinedRegion',
            addressCountry: product?.country || 'US',
          },
          deliveryTime: {
            '@type': 'ShippingDeliveryTime',
            handlingTime: {
              '@type': 'QuantitativeValue',
              minValue: '0',
              maxValue: '5',
              unitCode: 'DAY',
            },
            transitTime: {
              '@type': 'QuantitativeValue',
              minValue: '1',
              maxValue: '7',
              unitCode: 'DAY',
            },
            cutoffTime: '20:00-09:00',
            businessDays: {
              '@type': 'OpeningHoursSpecification',
              dayOfWeek: [
                'https://schema.org/Monday',
                'https://schema.org/Tuesday',
                'https://schema.org/Wednesday',
                'https://schema.org/Thursday',
                'https://schema.org/Friday',
              ],
            },
          },
        },
        hasMerchantReturnPolicy: {
          '@type': 'MerchantReturnPolicy',
          refundType: 'FullRefund',
          applicableCountry: product?.country || 'US',
          restockingFee: {
            '@type': 'MonetaryAmount',
            value: '0.00',
            currency: product?.price?.currency || 'USD',
          },
          returnShippingFeesAmount: {
            '@type': 'MonetaryAmount',
            value: '0.00',
            currency: product?.price?.currency || 'USD',
          },
          returnPolicyCountry: product?.country || 'US',
          returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
          merchantReturnDays: 30,
          returnMethod: 'https://schema.org/ReturnByMail',
        },
      };

      offers.push(offer);
    }

    return offers;
  }

  private generateReviews(product?: Product | null): Array<Review> {
    const reviews = new Array(0);

    if (product) {
      const phoneLanguage = `${
        product?.language || this.translateService.getActiveLang() || 'en'
      }`;
      const phoneModel = `${product?.model || ''}`;

      const stateReviews = this.akitaFeaturedQuery.getFeaturedReviews(
        phoneLanguage,
        phoneModel
      );

      for (const reviewItem of stateReviews) {
        if (reviewItem?.rating) {
          const review: Review = {
            '@type': 'Review',
            author: {
              '@type': 'Person',
              name: reviewItem?.author || 'anonymous',
            },
            datePublished: reviewItem?.date ? reviewItem.date.toISOString() : undefined,
            name: reviewItem?.title || '',
            reviewBody: reviewItem?.description || reviewItem?.title || '',
            reviewRating: {
              '@type': 'Rating',
              bestRating: '5',
              ratingValue: `${reviewItem?.rating || ''}`,
              worstRating: '1',
            },
          };

          reviews.push(review);
        }
      }
    }

    return reviews;
  }

  private generateAggregateRating(product?: Product | null): AggregateRating | null {
    let aggregateRating: AggregateRating | null = null;

    if (product) {
      const phoneLanguage = `${
        product?.language || this.translateService.getActiveLang() || 'en'
      }`;
      const phoneModel = `${product?.model || ''}`;

      const stateReviews = this.akitaFeaturedQuery.getFeaturedReviews(
        phoneLanguage,
        phoneModel
      );

      let totalValue = 0;
      let reviewCount = 0;

      for (const reviewItem of stateReviews) {
        if (reviewItem?.rating) {
          totalValue = reviewItem.rating + totalValue;
          reviewCount += 1;
        }
      }

      if (totalValue && reviewCount) {
        aggregateRating = {
          '@type': 'AggregateRating',
          ratingValue: `${(totalValue / reviewCount).toFixed(1)}`,
          reviewCount: `${reviewCount || 0}`,
        };
      }
    }

    return aggregateRating;
  }

  private get priceValidUntil(): string | undefined {
    return addMonths(new Date(), EXPIRING_MONTHS).toISOString();
  }

  private getPlaceholderDescription(product?: Product | null): string {
    const username = 'Popsy';
    let productTitle = '';
    const message = this.translateService.translate('msg_empty_description_long');

    if (product && product.title) {
      productTitle = product.title;
    }

    return `${username} ${message} ${productTitle}`;
  }

  public ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();

    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }
}
